<template>
  <div class="documents-form">
    <!-- Read-only state when documentation exists -->
    <div v-if="existingDocumentation" class="documents-form__readonly">
      <div class="documents-form__readonly-header">
        <i class="pi pi-check documents-form__readonly-icon" />
        <span class="documents-form__readonly-title">Dokumentasjon lagret</span>
      </div>
      <div class="documents-form__readonly-description">
        Dokumentasjonen har blitt lagret og kan ikke endres.
      </div>
      <div
        v-if="existingDocumentation.description"
        class="documents-form__readonly-comment"
      >
        <strong>Beskrivelse:</strong>
        <p>{{ existingDocumentation.description }}</p>
      </div>
      <div class="documents-form__readonly-files">
        <strong>Vedlagte filer:</strong>
        <ul>
          <li
            v-for="file in existingDocumentation.attachments"
            :key="file.fileName"
          >
            {{ file.fileName }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Form for creating documentation -->
    <Form
      v-else
      v-slot="$form"
      :initial-values="formValues"
      :resolver="resolver"
      :validate-on-value-update="true"
      :validate-on-blur="true"
      class="documents-form__form"
      @submit="onFormSubmit"
    >
      <!-- Validation Summary -->
      <Message
        v-if="
          attemptedSubmit &&
          (selectedFiles.length === 0 || $form.description?.invalid)
        "
        severity="error"
        icon="pi pi-exclamation-triangle"
        class="documents-form__validation-summary"
      >
        <div class="documents-form__validation-content">
          <strong>Kan ikke lagre dokumentasjon</strong>
          <p v-if="selectedFiles.length === 0">
            Du må laste opp minst én fil før du kan lagre.
          </p>
          <p v-if="$form.description?.invalid">
            Du må fylle ut en gyldig beskrivelse.
          </p>
        </div>
      </Message>

      <!-- Description Section -->
      <div class="documents-form__field-group">
        <label for="description" class="documents-form__label">
          Beskrivelse
          <span class="documents-form__required">*</span>
        </label>
        <Textarea
          v-model="formValues.description"
          name="description"
          rows="3"
          :max-rows="5"
          placeholder="Legg til en beskrivelse av dokumentasjonen..."
          :disabled="isSubmitting"
          :invalid="$form.description?.invalid"
          class="documents-form__textarea"
          auto-resize
        />
        <Message
          v-if="$form.description?.invalid"
          severity="error"
          size="small"
          variant="simple"
          class="documents-form__error"
        >
          {{ $form.description.error?.message }}
        </Message>
      </div>

      <!-- File Upload Section -->
      <div class="documents-form__field-group">
        <label class="documents-form__label">
          Last opp filer
          <span class="documents-form__required">*</span>
        </label>
        <!-- Hidden file input for manual triggering -->
        <input
          ref="hiddenFileInput"
          type="file"
          :multiple="true"
          :accept="'.pdf,.jpg,.jpeg,.png,.doc,.docx'"
          style="display: none"
          @change="onHiddenFileInputChange"
        />
        <FileUpload
          ref="fileUploadRef"
          name="files"
          :custom-upload="true"
          :multiple="true"
          :file-limit="5"
          :max-file-size="10485760"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          :disabled="isSubmitting"
          class="documents-form__file-upload"
          @select="onFileSelect"
          @remove="onFileRemove"
          @clear="onFilesClear"
        >
          <template #header>
            <div class="documents-form__upload-header">
              <i class="pi pi-cloud-upload documents-form__upload-icon" />
              <span>Klikk for å laste opp eller dra og slipp</span>
              <BaseButton
                v-if="selectedFiles.length > 0"
                text="Fjern alle"
                :icon="CloseIcon"
                variant="tertiary"
                :uppercase="true"
                size="xs"
                @click="onFilesClear"
              />
              <BaseButton
                v-if="selectedFiles.length > 0 && selectedFiles.length < 5"
                text="Velg filer"
                :icon="AddIcon"
                variant="tertiary"
                :uppercase="true"
                size="xs"
                @click="triggerFileInput"
              />
            </div>
          </template>
          <template #content>
            <div
              v-if="selectedFiles.length === 0"
              class="documents-form__upload-empty"
            >
              <p>Støttede formater: PDF, JPG, PNG, DOC, DOCX</p>
              <p>Maksimal filstørrelse: 10 MB per fil</p>
              <BaseButton
                text="Velg filer"
                :icon="AddIcon"
                variant="tertiary"
                :uppercase="true"
                class="documents-form__upload-button"
                @click="triggerFileInput"
              />
            </div>
            <div v-else class="documents-form__upload-files">
              <div
                v-for="file in selectedFiles"
                :key="file.name"
                class="documents-form__upload-file"
              >
                <i class="pi pi-file documents-form__upload-file-icon" />
                <span class="documents-form__upload-file-name">{{
                  file.name
                }}</span>
                <span class="documents-form__upload-file-size">
                  ({{ formatFileSize(file.size) }})
                </span>
                <BaseButton
                  :icon="CloseIcon"
                  variant="tertiary"
                  size="xs"
                  :rounded="true"
                  shape="circle"
                  class="documents-form__upload-file-delete"
                  @click="removeFile(file)"
                />
              </div>
            </div>
          </template>
        </FileUpload>
      </div>

      <!-- Form Actions -->
      <div class="documents-form__actions">
        <BaseButton
          text="Avbryt"
          variant="tertiary"
          :uppercase="true"
          :disabled="isSubmitting"
          @click="handleCancel"
        />
        <BaseButton
          text="Lagre dokumentasjon"
          type="submit"
          :uppercase="true"
          :is-loading="isSubmitting"
          :disabled="selectedFiles.length === 0"
        />
      </div>

      <!-- Confirmation Dialog -->
      <Dialog
        :visible="showConfirmDialog"
        modal
        header="Bekreft lagring"
        :style="{ width: '450px' }"
        :closable="false"
        @update:visible="showConfirmDialog = $event"
      >
        <div class="documents-form__confirm-content">
          <i class="pi pi-info-circle documents-form__confirm-icon" />
          <div>
            <p>
              Når dokumentasjonen er lagret, kan den ikke redigeres eller
              erstattes. Er du sikker på at du vil fortsette?
            </p>
          </div>
        </div>
        <template #footer>
          <BaseButton
            text="Avbryt"
            variant="tertiary"
            :uppercase="true"
            @click="showConfirmDialog = false"
          />
          <BaseButton
            text="Lagre dokumentasjon"
            :uppercase="true"
            @click="handleConfirmedSubmit"
          />
        </template>
      </Dialog>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { Form } from "@primevue/forms";
import Textarea from "primevue/textarea";
import FileUpload from "primevue/fileupload";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import { useServiceRecordsApi } from "@/services/api/useServiceRecordsApi";
import { useDocumentationCache } from "@/composables/useDocumentationCache";
import type {
  DocumentationRecord,
  CreateDocumentationDto,
  FileSelectEvent,
} from "@/types/service-records";
import { getMessageAttachmentDto } from "@/utilities/fileUtils";
import AddIcon from "@/assets/img/Plus.svg";
import CloseIcon from "@/assets/img/Close.svg";

interface Props {
  serviceOfferId: number;
  tenderId: number;
  supplierRequestId: number;
  customerOrgNumber: string;
  supplierId: number;
  existingDocumentation?: DocumentationRecord | null;
}

interface FormData {
  description: string;
}

interface Emits {
  (e: "hide"): void;
  (e: "documentation-created", documentation: DocumentationRecord): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Composables
const { createDocumentation } = useServiceRecordsApi();
const { setCachedDocumentation } = useDocumentationCache();

// Reactive state
const selectedFiles = ref<File[]>([]);
const isSubmitting = ref(false);
const attemptedSubmit = ref(false);
const showConfirmDialog = ref(false);
const fileUploadRef = ref();
const hiddenFileInput = ref<HTMLInputElement>();
const formValues = ref<FormData>({
  description: "",
});

const resolver = ({ values }: { values: Record<string, unknown> }) => {
  const errors: Record<string, Array<{ message: string }>> = {};

  // Description validation
  const description = values.description as string | undefined;
  if (!description || description.trim().length === 0) {
    errors.description = [{ message: "Beskrivelse er påkrevd" }];
  } else if (description.length > 500) {
    errors.description = [
      { message: "Beskrivelsen kan ikke være lengre enn 500 tegn" },
    ];
  }

  return { errors };
};

// Computed properties
const hasValidFiles = computed(() => selectedFiles.value.length > 0);

// File handling
const onFileSelect = (event: FileSelectEvent) => {
  // Prevent duplicate files by checking if they already exist
  const newFiles = event.files.filter(
    (newFile) =>
      !selectedFiles.value.some(
        (existingFile) =>
          existingFile.name === newFile.name &&
          existingFile.size === newFile.size &&
          existingFile.lastModified === newFile.lastModified,
      ),
  );

  // Respect the file limit (5 files max)
  const remainingSlots = 5 - selectedFiles.value.length;
  const filesToAdd = newFiles.slice(0, remainingSlots);

  selectedFiles.value = [...selectedFiles.value, ...filesToAdd];
};

const onFileRemove = (event: { file: File }) => {
  selectedFiles.value = selectedFiles.value.filter(
    (file) => file.name !== event.file.name,
  );
};

const onFilesClear = () => {
  selectedFiles.value = [];
};

const removeFile = (fileToRemove: File) => {
  selectedFiles.value = selectedFiles.value.filter(
    (file) => file !== fileToRemove,
  );
};

const onHiddenFileInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    const fileArray = Array.from(target.files);
    const mockEvent = { files: fileArray };
    onFileSelect(mockEvent as FileSelectEvent);
    // Clear the input so the same file can be selected again
    target.value = "";
  }
};

const triggerFileInput = () => {
  // Use our hidden file input for reliable file selection
  if (hiddenFileInput.value) {
    hiddenFileInput.value.click();
  }
};

// Form handlers
const onFormSubmit = () => {
  attemptedSubmit.value = true;

  if (!hasValidFiles.value) {
    return;
  }

  showConfirmDialog.value = true;
};

const handleConfirmedSubmit = async () => {
  showConfirmDialog.value = false;
  isSubmitting.value = true;

  try {
    // Convert File objects to FileAttachment DTOs
    const attachments = await Promise.all(
      selectedFiles.value.map((file) => getMessageAttachmentDto(file)),
    );

    const formData: CreateDocumentationDto = {
      attachmentsDescription: formValues.value.description,
      attachments,
    };

    const result = await createDocumentation(
      props.supplierId,
      props.supplierRequestId,
      props.serviceOfferId,
      [formData],
    );

    // Only emit success event if creation was successful and we have data
    if (result?.data && Array.isArray(result.data)) {
      // API returns array of attachments, construct a proper DocumentationRecord
      const now = new Date().toISOString();
      const documentationRecord: DocumentationRecord = {
        id: Date.now(), // Generate a temporary ID
        description: formValues.value.description,
        attachments: result.data, // Use the entire array of attachments
        createdAt: now,
        isImmutable: false,
        serviceOfferId: props.serviceOfferId,
        supplierId: props.supplierId,
      };

      // Cache the documentation data for immediate display
      setCachedDocumentation({
        description: documentationRecord.description || "",
        attachments: documentationRecord.attachments,
        createdAt: documentationRecord.createdAt,
        serviceOfferId: props.serviceOfferId,
      });

      emit("documentation-created", documentationRecord);
    } else {
      console.warn("❌ No data in API response, not emitting success event");
    }
    attemptedSubmit.value = false; // Reset validation state on success
  } catch (error) {
    console.error("❌ Failed to create documentation:", error);
    // Don't emit success events when there's an error
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  emit("hide");
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};
</script>

<style lang="scss" scoped>
.documents-form {
  padding: 0.5rem;
  @media (max-width: $bp-sm) {
    padding: 1rem;
  }

  &__readonly {
    text-align: center;
    padding: 2rem;
    background: $color-success-light;
    border-radius: 8px;
    border: 1px solid $color-success;

    &-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
    }

    &-icon {
      color: $color-success;
      font-size: 1.25rem;
    }

    &-title {
      font-weight: 600;
      color: $color-success-dark;
      font-size: 1.125rem;
    }

    &-description {
      color: $color-success-dark;
      margin-bottom: 1.5rem;
    }

    &-comment,
    &-files {
      text-align: left;
      background: $color-white;
      padding: 1rem;
      border-radius: 6px;
      margin-bottom: 1rem;

      strong {
        color: $color-primary-darker;
        display: block;
        margin-bottom: 0.5rem;
      }

      p {
        margin: 0;
        white-space: pre-wrap;
      }

      ul {
        margin: 0;
        padding-left: 1.5rem;

        li {
          margin-bottom: 0.25rem;
        }
      }
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__validation-summary {
    margin-bottom: 1rem;
  }

  &__validation-content {
    strong {
      display: block;
      margin-bottom: 0.5rem;
    }

    p {
      margin: 0;
    }
  }

  &__field-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__label {
    font-weight: 600;
    color: $color-primary-darker;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  &__required {
    color: $color-error;
  }

  &__textarea {
    width: 100%;
  }

  &__error {
    margin-top: 0.25rem;
  }

  &__file-upload {
    width: 100%;
  }

  &__upload-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  &__upload-icon {
    color: $color-primary;
  }

  &__upload-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: $color-text-secondary;
    padding: 2rem;
  }

  &__upload-button {
    margin-top: 1rem;
    width: fit-content;
  }
  &__upload-files {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  &__upload-file {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: $color-primary-lighter;
    border-radius: 6px;

    &-icon {
      color: $color-primary;
      flex-shrink: 0;
    }

    &-name {
      flex: 1;
      font-weight: 500;
      color: $color-primary-darker;
    }

    &-size {
      color: $color-text-secondary;
      font-size: 0.875rem;
    }

    &-delete {
      flex-shrink: 0;
      color: $color-error;

      &:hover {
        background-color: $color-error-light;
      }
    }
  }

  &__actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1rem;

    @media (max-width: $bp-sm) {
      flex-direction: column;
    }
  }

  &__confirm-content {
    display: flex;
    gap: 1rem;
    align-items: flex-start;

    p {
      margin: 0;
      line-height: 1.5;
    }
  }

  &__confirm-icon {
    color: $color-info;
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
}
</style>
